import {Table} from 'antd';
import {ColumnsType} from 'antd/lib/table';
import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {selectFields} from '../../../containers/QueryBuilder/queryBuilderSlice';
import {Cell, QueryResult, QueryStatus} from '../../../containers/QueryBuilder/types/Query';
import {useAppSelector} from '../../../hooks';
import {QueryBuilderContext, QueryBuilderContextType} from '../QueryBuilderContext';
import {calculateExpectedResult} from "../QueryBuilderUtil";

import styles from "./ResultTable.module.scss";
import {CalculateEarliestYearOfDatasetPeriod} from "./DateRange";
import ResultStatusMessage, {ResultStatus} from './ResultStatusMessage';
import _ from "lodash";
import {apiFetchQueryMetaData, ChartOptions} from "../../../actions/queryBuilderApi";
import {ChartType} from "../../../containers/QueryBuilder/visualisations/types/ChartType";
import {useChartDataFetcher} from "../../../containers/QueryBuilder/visualisations/charts/chartDataFetcher";
import {QueryBuilderResultsSkeleton} from "../QueryBuilderResultsSkeleton";

export const DATA_TABLE_TEST_ID = 'data-table'
export const EMPTY_TABLE_TEST_ID = 'empty-table'

export const INACTIVE_QUERY_STATUS_LIST = [QueryStatus.READY, QueryStatus.NOT_READY]
export const ACTIVE_QUERY_STATUS_LIST = [QueryStatus.RUNNING, QueryStatus.FINISHED]

// region utility functions
export const compare = <T extends unknown>(a: T | null, b: T | null): number => {

    const REGEX_NUM = /[',$£€¥%\u2009\u202F\u20BD\u20a9\u20BArfkɃΞ]/gi

    let result = 0
    if (a === b) {
        result = 0
    } else if (!a) {
        result = -1
    } else if (!b) {
        result = 1
    } else if (typeof a === 'string' && typeof b === 'string') {
        const aN = _.toNumber(a.replace(REGEX_NUM, ''))
        const bN = _.toNumber(b.replace(REGEX_NUM, ''))
        if (_.isFinite(aN) && _.isFinite(bN)) {
            result = aN - bN
        } else if (!_.isFinite(aN) && !_.isFinite(bN)) {
            if (a === 'NULL') a = null
            if (b === 'NULL') b = null
            if (!a) {
                result = -1
            } else if (!b) {
                result = 1
            } else {
                result = a.localeCompare(b, 'standard', {sensitivity: 'case'})
            }
        } else if (!_.isFinite(aN)) {
            result = -1
        } else if (!_.isFinite(bN)) {
            result = 1
        }
    } else {
        // won't happen as all values are strings
        result = b > a ? -1 : 1
    }
    return result
}

const middleTruncation = (title: string) => {
    const MAX_LENGTH = 50;
    if (title.length <= MAX_LENGTH) return title;

    // minus 3 for the '...'
    const keep = Math.floor((MAX_LENGTH - 3) / 2);
    const start = title.slice(0, keep);
    const end = title.slice(-keep);

    return `${start}...${end}`;

}

export const headerMaker = (headers: Cell[][], row: number, col: number): ColumnsType => {
    const children = row === headers.length - 1 ? [] : [headerMaker(headers, row + 1, col)]
    const title = (headers[row][col] ? headers[row][col].value : '') || '';

    return {
        title: middleTruncation(String(title)),
        dataIndex: `${col}`,
        children: children,
        width: 150,
        render: (o: Cell) => o ? o.value : '',
        sorter: (left: Cell[], right: Cell[]) => {
            // check column exists in cell array
            if (left.length <= col || right.length <= col) {
                return 0;
            }
            if (left[col].order || right[col].order) {
                return compare(left[col].order, right[col].order)
            } else {
                return compare(left[col].value, right[col].value)
            }

        },
    } as unknown as ColumnsType
}

export const rawContentHasEntries = (queryResult: QueryResult): boolean => {
    return queryResult.rawContent && queryResult.rawContent.length > 0
}

// endregion utility functions

interface ResultTableProps {
    isCollapsed: boolean
    isCohortAnalyser: boolean
}

const ResultTable = ({isCollapsed, isCohortAnalyser}: ResultTableProps) => {

    const {
        userQuery,
        queryResult,
        setDefaultQueryVisualisationOptions,
        setDefaultQueryVisualisationResults,
        setQueryMetaData
    } = useContext(QueryBuilderContext) as QueryBuilderContextType

    const [haveTimeFrameFiltersExpired, setHaveTimeFrameFiltersExpired] = useState<boolean>(false)

    const earliestYearOfDatasetPeriod = CalculateEarliestYearOfDatasetPeriod()

    const extractYearValue = (yearString: string): number | null => {
        const match = yearString.match(/\d{4}/)
        return match ? parseInt(match[0], 10) : null
    }

    const checkQueryTimeFrameFilters = useCallback((earliestYearOfDatasetPeriod: number) => {

            let outDated = false

            if (userQuery.query && earliestYearOfDatasetPeriod !== 0) {
                if (
                    (!userQuery.query.filterLabels || userQuery.query.filterLabels.length <= 0) &&
                    userQuery.query.filters &&
                    userQuery.query.filters.length > 0
                ) {
                    for (const filter of userQuery.query.filters) {
                        if ((filter.code.includes('year') ||
                            filter.code.includes('period') ||
                            filter.code.includes('date')
                        ) && filter.value) {
                            const intValue = extractYearValue(filter.value)
                            if (intValue && intValue < earliestYearOfDatasetPeriod) {
                                outDated = true
                                break
                            }
                        }
                    }
                }
            }
            return outDated;
        },
        [userQuery.query]
    )

    const columnsKey = JSON.stringify(userQuery?.query?.columns)
    const rowsKey = JSON.stringify(userQuery?.query?.rows)
    const valuesKey = JSON.stringify(userQuery?.query?.values)

    const defaultChartOptions = useMemo<ChartOptions | undefined>(() => {

        if (!queryResult || !queryResult?.valueAvailable || !rawContentHasEntries(queryResult) || !userQuery) {
            return undefined
        }

        const {columns, rows, values} = userQuery.query
        const defaultCategory = columns[0] || rows[0]
        const defaultValue = values[0]

        return {
            queryId: userQuery.queryId,
            chartType: ChartType.BAR,
            xFields: defaultCategory ? [defaultCategory] : [],
            yFields: [],
            values: defaultValue ? [defaultValue] : [],
            filters: {},
            sort: 0,
            topTen: false,
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        userQuery?.queryId,
        queryResult?.valueAvailable,
        columnsKey,
        rowsKey,
        valuesKey
    ])

    const defaultChartResults = useChartDataFetcher(defaultChartOptions)

    useEffect(() => {
        if (defaultChartOptions) {
            setDefaultQueryVisualisationOptions(defaultChartOptions)
        }
    }, [defaultChartOptions, setDefaultQueryVisualisationOptions])

    useEffect(() => {
        if (defaultChartResults) {
            setDefaultQueryVisualisationResults(defaultChartResults)
        }
    }, [defaultChartResults, setDefaultQueryVisualisationResults])

    useEffect(() => {
        let isCancelled = false
        if (userQuery?.queryId && queryResult) {
            apiFetchQueryMetaData(userQuery.queryId)
                .then((metaData) => {
                    if (!isCancelled) {
                        setQueryMetaData(metaData)
                    }
                })
                .catch((error) => {
                    console.error("Failed to fetch query metadata:", error)
                })
        } else {
        }

        return () => {
            isCancelled = true
        }
    }, [userQuery?.queryId, setQueryMetaData, queryResult])

    useEffect(() => {
        const areTimeFrameFiltersOutdated = checkQueryTimeFrameFilters(earliestYearOfDatasetPeriod)
        if (haveTimeFrameFiltersExpired !== areTimeFrameFiltersOutdated) {
            setHaveTimeFrameFiltersExpired(areTimeFrameFiltersOutdated)
        }
    }, [checkQueryTimeFrameFilters, earliestYearOfDatasetPeriod, haveTimeFrameFiltersExpired])

    const elem = document.querySelector(`.${styles.result_container}`)
    //  @ts-ignore
    const rect = elem ? elem.getBoundingClientRect() : {height: 0}
    const fields = useAppSelector(selectFields)!

    // endregion utility functions

    if (queryResult) { // if the backend has processed the query without errors

        if (queryResult.valueAvailable) { // if query results are within display limit (including 0 results)

            if (rawContentHasEntries(queryResult)) { // if there is at least 1 row of results
                let columns: ColumnsType[] = []
                if (queryResult && queryResult.rawHeaders) {
                    for (let i = 0; i < queryResult.rawHeaders[0].length; i++) {
                        columns.push(headerMaker(queryResult.rawHeaders, 0, i))
                    }
                }
                const offset = 200;
                const rowHeight = 40;
                const numberOfRowsOnScreen = Math.max(Math.floor((rect.height - offset) / rowHeight), 5);

                return <Table data-testid={DATA_TABLE_TEST_ID}
                              bordered size="small"
                              pagination={{pageSize: numberOfRowsOnScreen, simple: true}}
                              scroll={{x: '100%'}}
                              rowKey={i => i[0].id}
                              dataSource={queryResult.rawContent}
                              columns={columns}
                              style={{whiteSpace: "nowrap"}}
                />

            } else { // valid return when there are 0 results for a query
                return <ResultStatusMessage queryId={userQuery.queryId} resultStatus={ResultStatus.ZeroResultsReturned}/>
            }

        }

        if (userQuery.errorMsg === 'DISPLAY_CAPACITY_EXCEEDED' || userQuery.largeExport) {
            return <ResultStatusMessage queryId={userQuery.queryId} resultStatus={ResultStatus.DisplayCapacityExceeded}/>
        }

    }

    if (userQuery.errorMsg && userQuery.errorMsg.includes('RESULT_CAPACITY_EXCEEDED')) {
        return <ResultStatusMessage queryId={userQuery.queryId} resultStatus={ResultStatus.ResultsCapacityExceeded}/>
    }

    // expired saved results cache
    if (queryResult?.message?.includes('404')) {
        return <ResultStatusMessage queryId={userQuery.queryId}
                                    resultStatus={ResultStatus.SavedResultsExpired}
                                    haveTimeFrameFiltersExpired={haveTimeFrameFiltersExpired}
        />
    }

    // result table behavior based on query status i.e. when query hasn't run yet, canceled or there is a server error
    if (INACTIVE_QUERY_STATUS_LIST.includes(userQuery.status)) {

        const preview = calculateExpectedResult(fields, userQuery.query)
        if (preview.headers) {
            let columns: ColumnsType[] = []
            if (preview.headers.length > 0) {
                for (let i = 0; i < preview.headers[0].length; i++) {
                    columns.push(headerMaker(preview.headers, 0, i))
                }
            }
            return <Table data-testid={DATA_TABLE_TEST_ID}
                          bordered
                          size='small'
                          pagination={{pageSize: !isCollapsed ? 50 : 75, simple: true}}
                          scroll={{y: !isCollapsed ? rect.height - 250 : rect.height - 200, x: '100%'}}
                          rowKey={i => i[0].id}
                          dataSource={preview.values}
                          columns={columns}
                          style={{whiteSpace: 'nowrap'}}
            />

        }

    }

    if (ACTIVE_QUERY_STATUS_LIST.includes(userQuery.status)) {
        return <div>
            <QueryBuilderResultsSkeleton data-testid={'query-skeleton'} queryStatus={userQuery.status} isCohortAnalyser={isCohortAnalyser} />
        </div>
    }

    if (userQuery.status === QueryStatus.ERROR) {
        return <ResultStatusMessage queryId={userQuery.queryId} resultStatus={ResultStatus.ServerError}/>
    }

    if (userQuery.status === QueryStatus.CANCELLED) {
        return <ResultStatusMessage queryId={userQuery.queryId} resultStatus={ResultStatus.QueryCancelled}/>
    }

    return <div data-testid={EMPTY_TABLE_TEST_ID}></div>

}

export default ResultTable